import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { ArrowLeft, ArrowRight, Send, Upload, Film, Check, Loader2, Download, ExternalLink } from "lucide-react";
import { TRUE_COLORS_SOLUTIONS, TC_LOGO_URL, FREEBIE_PDF_URL, FREEBIE_TITLE, RECOMMENDATION_OPTIONS, COUNTRIES, US_STATES } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;

// ----- step config -----
const QUESTIONS = [
  {
    key: "you",
    title: "Let's get to know you.",
    subtitle: "Just the basics so we know who we're hearing from.",
    counted: true,
  },
  {
    key: "before",
    title: "What was happening in your organization that led you to connect with TCI?",
    subtitle: "Set the scene. Big or small, this is where it starts.",
    counted: true,
  },
  {
    key: "program",
    title: "Which True Colors experience(s) did you have?",
    subtitle: "Tap all that apply, you can pick more than one. Then let us know who walked through it with you.",
    counted: true,
  },
  {
    key: "after",
    title: "What's different for your team now?",
    subtitle: "Tell us what shifted. Energy, conversations, behavior, results, anything.",
    counted: true,
  },
  {
    key: "pitch",
    title: "If a colleague or friend asked you whether they should do this too, what would you tell them?",
    subtitle: "Your honest answer is the most useful thing here.",
    counted: true,
  },
  {
    key: "media",
    title: "Would you be willing to share a photo or short video from your experience?",
    subtitle: "One more, if you're up for it. Totally optional, but we love these.",
    counted: false,
  },
  {
    key: "consent",
    title: "How can we share your story?",
    subtitle: "You're in control. Pick what feels right.",
    counted: true,
  },
];

const CONSENT_OPTIONS = [
  { value: "approved_named",      label: "With my name",   blurb: "Use my name and organization when sharing." },
  { value: "approved_anonymized", label: "Anonymized",     blurb: "Share the story, just keep my name out of it." },
  { value: "private",             label: "Just between us", blurb: "Keep this private to True Colors. Don't share publicly." },
];

const initial = {
  submitter_first_name: "",
  submitter_last_initial: "",
  submitter_role: "",
  submitter_org: "",
  submitter_email: "",
  state: "",
  country: "",
  rep_id: "",
  other_rep_name: "",
  before_text: "",
  programs: [],
  people_trained: "",
  training_date: "",
  delivery_format: "",
  engagement_type: "",
  after_text: "",
  recommendation: "",
  recommendation_reason: "",
  media: [],
  client_logo: null,
  logo_share_consent: false,
  consent_status: "",
};

export default function PublicShare() {
  const [phase, setPhase] = useState("welcome"); // welcome | question | thanks
  const [step, setStep] = useState(0); // 0..6 within QUESTIONS
  const [data, setData] = useState(initial);
  const [team, setTeam] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/team`).then(({ data: d }) => setTeam(d.team || []));
  }, []);

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  // ----- step counting (display) -----
  const totalNumbered = useMemo(() => QUESTIONS.filter((q) => q.counted).length, []);
  const numberFor = (idx) => {
    let n = 0;
    for (let i = 0; i <= idx; i++) if (QUESTIONS[i].counted) n++;
    return n;
  };

  const currentQuestion = QUESTIONS[step];

  // ----- validation per step -----
  const stepInvalid = useMemo(() => {
    const d = data;
    switch (currentQuestion?.key) {
      case "you":
        return !d.submitter_first_name.trim() || !d.submitter_last_initial.trim() || !d.submitter_org.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.submitter_email);
      case "before":  return !d.before_text.trim();
      case "program":
        if (!d.programs || d.programs.length === 0) return true;
        if (d.rep_id === "OTHER") return !d.other_rep_name.trim();
        return !d.rep_id;
      case "after":   return !d.after_text.trim();
      case "pitch":
        if (!d.recommendation) return true;
        if (d.recommendation === "no" && !d.recommendation_reason.trim()) return true;
        return false;
      case "media":   return false; // optional
      case "consent": return !d.consent_status;
      default:        return false;
    }
  }, [data, currentQuestion]);

  const next = () => setStep((s) => Math.min(QUESTIONS.length - 1, s + 1));
  const back = () => {
    if (step === 0) setPhase("welcome");
    else setStep((s) => s - 1);
  };

  const handleMediaUpload = async (file) => {
    setError("");
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      setError("That file is over 25MB. Try a smaller one.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: asset } = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setData((d) => ({ ...d, media: [...d.media, asset] }));
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload didn't go through. Try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleLogoUpload = async (file) => {
    setError("");
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      setError("Logo is over 25MB. Try a smaller one.");
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: asset } = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setData((d) => ({ ...d, client_logo: asset }));
    } catch (e) {
      setError(e?.response?.data?.detail || "Logo upload didn't go through. Try again.");
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const lastInitial = data.submitter_last_initial.trim().slice(0, 1).toUpperCase();
      const body = {
        submitter_name: `${data.submitter_first_name.trim()}${lastInitial ? ` ${lastInitial}.` : ""}`,
        submitter_role: data.submitter_role.trim(),
        submitter_org: data.submitter_org.trim(),
        submitter_email: data.submitter_email.trim(),
        state: data.state.trim() || null,
        country: data.country.trim() || null,
        rep_id: data.rep_id === "OTHER" ? null : data.rep_id,
        other_rep_name: data.rep_id === "OTHER" ? data.other_rep_name.trim() : null,
        before_text: data.before_text.trim(),
        programs: data.programs,
        people_trained: data.people_trained.trim() || null,
        training_date: data.training_date.trim() || null,
        delivery_format: data.delivery_format || null,
        engagement_type: data.engagement_type.trim() || null,
        after_text: data.after_text.trim(),
        recommendation: data.recommendation,
        recommendation_reason: data.recommendation === "no" ? data.recommendation_reason.trim() : null,
        media: data.media,
        client_logo: data.logo_share_consent ? data.client_logo : null,
        logo_share_consent: !!data.logo_share_consent,
        consent_status: data.consent_status,
      };
      await axios.post(`${API}/testimonial`, body);
      // celebrate
      const colors = ["#f68b1f", "#ffd400", "#7ac143", "#0091be"];
      confetti({ particleCount: 130, spread: 90, origin: { y: 0.6 }, colors });
      setPhase("thanks");
    } catch (e) {
      setError(e?.response?.data?.detail || "Something hiccuped. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ===== UI =====

  if (phase === "welcome") return <Welcome onStart={() => setPhase("question")} />;
  if (phase === "thanks")  return <ThankYou />;

  const progress = Math.round(((step + 1) / QUESTIONS.length) * 100);
  const isLast = step === QUESTIONS.length - 1;

  return (
    <div className="min-h-screen bg-[var(--tc-bg-soft)] flex flex-col">
      <Header />
      <main className="flex-1 max-w-2xl w-full mx-auto px-4 sm:px-6 pt-4 pb-12">
        {/* progress */}
        <div className="sticky top-0 z-30 bg-[var(--tc-bg-soft)]/90 backdrop-blur py-3 mb-5 -mx-4 sm:-mx-6 px-4 sm:px-6">
          <div className="flex items-center justify-between mb-2">
            <button onClick={back} className="text-slate-500 hover:text-slate-900 flex items-center gap-1 text-sm font-bold" data-testid="share-back">
              <ArrowLeft size={16} /> Back
            </button>
            <div className="tc-eyebrow text-slate-500" data-testid="share-progress">
              {currentQuestion.counted ? `Question ${numberFor(step)} of ${totalNumbered}` : "Optional"} · {progress}%
            </div>
            <div className="w-12" />
          </div>
          <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
            <motion.div className="h-full rounded-full" style={{ background: "#f68b1f" }} animate={{ width: `${progress}%` }} transition={{ type: "spring", stiffness: 120, damping: 22 }} />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion.key}
            initial={{ x: 30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -30, opacity: 0 }}
            transition={{ type: "spring", stiffness: 180, damping: 22 }}
            className="tc-card p-6 sm:p-8"
            data-testid={`share-step-${currentQuestion.key}`}
          >
            <h1 className="font-display text-2xl sm:text-3xl text-tc-ink leading-tight">{currentQuestion.title}</h1>
            {currentQuestion.subtitle && <p className="text-slate-500 mt-1.5 text-sm sm:text-base">{currentQuestion.subtitle}</p>}

            <div className="mt-6 space-y-4">
              {/* Q1: about you */}
              {currentQuestion.key === "you" && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="First name" required>
                    <input value={data.submitter_first_name} onChange={(e) => update({ submitter_first_name: e.target.value })} placeholder="Sarah" data-testid="submitter-first-name" className={inputCls} />
                  </Field>
                  <Field label="Last initial" required hint="Just one letter, like M.">
                    <input
                      value={data.submitter_last_initial}
                      onChange={(e) => update({ submitter_last_initial: e.target.value.replace(/[^A-Za-z]/g, "").slice(0, 1).toUpperCase() })}
                      maxLength={1}
                      placeholder="M"
                      data-testid="submitter-last-initial"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Your role">
                    <input value={data.submitter_role} onChange={(e) => update({ submitter_role: e.target.value })} placeholder="Director of People" data-testid="submitter-role" className={inputCls} />
                  </Field>
                  <Field label="Organization" required>
                    <input value={data.submitter_org} onChange={(e) => update({ submitter_org: e.target.value })} placeholder="Acme Inc." data-testid="submitter-org" className={inputCls} />
                  </Field>
                  <Field label="Email" required>
                    <input type="email" value={data.submitter_email} onChange={(e) => update({ submitter_email: e.target.value })} placeholder="sarah@acme.com" data-testid="submitter-email" className={inputCls} />
                  </Field>
                  <Field label="Country">
                    <select value={data.country} onChange={(e) => update({ country: e.target.value, state: e.target.value === "United States" ? data.state : "" })} data-testid="submitter-country" className={inputCls}>
                      <option value="">Choose…</option>
                      {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label={data.country === "United States" ? "State" : "State / Region"}>
                    {data.country === "United States" ? (
                      <select value={data.state} onChange={(e) => update({ state: e.target.value })} data-testid="submitter-state" className={inputCls}>
                        <option value="">Choose…</option>
                        {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      <input value={data.state} onChange={(e) => update({ state: e.target.value })} placeholder="Region (optional)" data-testid="submitter-state" className={inputCls} />
                    )}
                  </Field>
                </div>
              )}

              {/* Q2: before */}
              {currentQuestion.key === "before" && (
                <textarea
                  value={data.before_text}
                  onChange={(e) => update({ before_text: e.target.value })}
                  placeholder="A few sentences is plenty."
                  data-testid="before-text"
                  className="w-full min-h-[160px] rounded-2xl border-2 border-slate-200 focus:border-orange-500 outline-none p-4 text-base leading-relaxed"
                />
              )}

              {/* Q3: program + rep */}
              {currentQuestion.key === "program" && (
                <div className="space-y-5">
                  <div>
                    <div className="tc-eyebrow text-slate-500 mb-2">Your True Colors experience(s) — tap all that apply</div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {TRUE_COLORS_SOLUTIONS.map((sol) => {
                        const selected = (data.programs || []).includes(sol);
                        return (
                          <button
                            key={sol}
                            onClick={() => {
                              const cur = data.programs || [];
                              const next = selected ? cur.filter((x) => x !== sol) : [...cur, sol];
                              update({ programs: next });
                            }}
                            data-testid={`program-${sol.toLowerCase().replace(/[^a-z0-9]/gi, "-")}`}
                            className={`text-left p-3.5 rounded-2xl border-2 text-sm font-bold transition-all flex items-start gap-2.5 ${selected ? "border-green-500 bg-green-50 text-green-900" : "border-slate-200 hover:border-slate-300"}`}
                          >
                            <span
                              className={`mt-0.5 w-5 h-5 rounded-md border-2 shrink-0 flex items-center justify-center ${selected ? "bg-green-500 border-green-500" : "border-slate-300"}`}
                              aria-hidden="true"
                            >
                              {selected && <Check size={14} className="text-white" />}
                            </span>
                            <span className="leading-snug">{sol}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="tc-eyebrow text-slate-500 mb-2">Who from our team worked with you?</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {team.map((u) => (
                        <button
                          key={u.id}
                          onClick={() => update({ rep_id: u.id, other_rep_name: "" })}
                          data-testid={`rep-${u.first_name.toLowerCase()}`}
                          className={`h-14 rounded-xl border-2 text-sm font-bold transition-all ${data.rep_id === u.id ? "border-blue-500 bg-blue-50 text-blue-900" : "border-slate-200 hover:border-slate-300"}`}
                        >
                          {u.first_name}
                        </button>
                      ))}
                      <button
                        onClick={() => update({ rep_id: "OTHER" })}
                        data-testid="rep-other"
                        className={`h-14 rounded-xl border-2 text-sm font-bold transition-all ${data.rep_id === "OTHER" ? "border-orange-500 bg-orange-50 text-orange-900" : "border-slate-200 hover:border-slate-300"}`}
                      >
                        Other
                      </button>
                    </div>
                    {data.rep_id === "OTHER" && (
                      <input
                        value={data.other_rep_name}
                        onChange={(e) => update({ other_rep_name: e.target.value })}
                        placeholder="Their name"
                        data-testid="other-rep-name"
                        className={`${inputCls} mt-2`}
                      />
                    )}
                  </div>

                  {/* Optional engagement details — helps the True Colors team
                      tell the full story without making the client think harder. */}
                  <div className="rounded-2xl border-2 border-dashed border-slate-200 p-4 sm:p-5 bg-slate-50/40">
                    <div className="tc-eyebrow text-slate-500 mb-1">A few quick details (optional)</div>
                    <p className="text-xs text-slate-500 mb-3">These help us remember the engagement. Skip anything you can't recall.</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <input
                        value={data.people_trained}
                        onChange={(e) => update({ people_trained: e.target.value })}
                        placeholder="How many people went through? e.g. ~50"
                        data-testid="people-trained"
                        className={inputCls}
                      />
                      <input
                        value={data.training_date}
                        onChange={(e) => update({ training_date: e.target.value })}
                        placeholder="When? e.g. March 2025"
                        data-testid="training-date"
                        className={inputCls}
                      />
                      <input
                        value={data.engagement_type}
                        onChange={(e) => update({ engagement_type: e.target.value })}
                        placeholder="What kind? e.g. Half-day workshop"
                        data-testid="engagement-type"
                        className={inputCls}
                      />
                      <div className="grid grid-cols-3 gap-2">
                        {["In-person", "Virtual", "Hybrid"].map((fmt) => (
                          <button
                            key={fmt}
                            type="button"
                            onClick={() => update({ delivery_format: data.delivery_format === fmt ? "" : fmt })}
                            data-testid={`format-${fmt.toLowerCase()}`}
                            className={`h-11 rounded-xl border-2 text-xs font-bold transition-all ${data.delivery_format === fmt ? "border-blue-500 bg-blue-50 text-blue-900" : "border-slate-200 hover:border-slate-300 text-slate-600"}`}
                          >
                            {fmt}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Q4: after */}
              {currentQuestion.key === "after" && (
                <textarea
                  value={data.after_text}
                  onChange={(e) => update({ after_text: e.target.value })}
                  placeholder="Paint the picture."
                  data-testid="after-text"
                  className="w-full min-h-[160px] rounded-2xl border-2 border-slate-200 focus:border-orange-500 outline-none p-4 text-base leading-relaxed"
                />
              )}

              {/* Q5: recommendation (NPS) */}
              {currentQuestion.key === "pitch" && (
                <div className="space-y-3">
                  {RECOMMENDATION_OPTIONS.map((opt) => {
                    const active = data.recommendation === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => update({ recommendation: opt.value, recommendation_reason: opt.value === "no" ? data.recommendation_reason : "" })}
                        data-testid={`recommendation-${opt.value}`}
                        className={`w-full text-left p-4 rounded-2xl border-2 transition-all flex items-start gap-3 ${active ? "bg-white" : "border-slate-200 hover:border-slate-300"}`}
                        style={active ? { borderColor: opt.color, boxShadow: `0 0 0 3px ${opt.color}22` } : {}}
                      >
                        <span
                          className="mt-0.5 w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center"
                          style={{ borderColor: active ? opt.color : "#CBD5E1", background: active ? opt.color : "transparent" }}
                        >
                          {active && <Check size={12} className="text-white" />}
                        </span>
                        <div>
                          <div className="font-bold text-base">{opt.label}</div>
                          <div className="text-xs text-slate-500 mt-0.5">{opt.blurb}</div>
                        </div>
                      </button>
                    );
                  })}

                  {data.recommendation === "no" && (
                    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
                      <div className="tc-eyebrow text-slate-500 mt-3 mb-1.5">Help us understand. What got in the way?</div>
                      <textarea
                        value={data.recommendation_reason}
                        onChange={(e) => update({ recommendation_reason: e.target.value })}
                        placeholder="A few sentences in your own words. We listen carefully to feedback."
                        data-testid="recommendation-reason"
                        className="w-full min-h-[120px] rounded-2xl border-2 border-slate-200 focus:border-orange-500 outline-none p-4 text-base leading-relaxed"
                      />
                    </motion.div>
                  )}
                </div>
              )}

              {/* media (bonus) */}
              {currentQuestion.key === "media" && (
                <>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="public-media-upload"
                    className="w-full h-32 rounded-2xl border-2 border-dashed border-slate-300 hover:border-orange-400 hover:bg-orange-50 transition-all flex flex-col items-center justify-center gap-1 text-slate-500"
                  >
                    {uploading ? <span className="font-bold">Uploading...</span> : (
                      <>
                        <Upload size={22} />
                        <span className="font-bold">Tap to add a photo or video</span>
                        <span className="text-xs">Up to 25MB</span>
                      </>
                    )}
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*,video/*" onChange={(e) => handleMediaUpload(e.target.files?.[0])} className="hidden" data-testid="public-media-input" />
                  {data.media.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                      {data.media.map((m, i) => (
                        <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-100 flex items-center justify-center text-slate-500">
                          {m.kind === "image" ? (
                            <span className="text-xs font-bold">Photo {i + 1}</span>
                          ) : (
                            <span className="text-xs font-bold flex items-center gap-1"><Film size={14} /> Video {i + 1}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* consent */}
              {currentQuestion.key === "consent" && (
                <div className="space-y-5">
                  <div className="space-y-2">
                    {CONSENT_OPTIONS.map((o) => (
                      <button
                        key={o.value}
                        onClick={() => update({ consent_status: o.value })}
                        data-testid={`public-consent-${o.value}`}
                        className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${data.consent_status === o.value ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}
                      >
                        <div className="font-bold">{o.label}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{o.blurb}</div>
                      </button>
                    ))}
                  </div>

                  {/* Logo permission + upload (only meaningful if not private) */}
                  {data.consent_status && data.consent_status !== "private" && (
                    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="border-t border-slate-200 pt-5">
                      <div className="tc-eyebrow text-slate-500 mb-2">One last thing</div>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={data.logo_share_consent}
                          onChange={(e) => update({ logo_share_consent: e.target.checked, client_logo: e.target.checked ? data.client_logo : null })}
                          data-testid="logo-share-consent"
                          className="mt-1 w-5 h-5 rounded-md accent-orange-500"
                        />
                        <span>
                          <span className="font-bold text-tc-ink">Yes, you may also use our company logo.</span>
                          <span className="block text-xs text-slate-500 mt-0.5">Optional. Uncheck if you'd rather not. You can upload it below.</span>
                        </span>
                      </label>

                      {data.logo_share_consent && (
                        <div className="mt-4">
                          <button
                            onClick={() => logoInputRef.current?.click()}
                            data-testid="logo-upload-button"
                            className="w-full h-24 rounded-2xl border-2 border-dashed border-slate-300 hover:border-orange-400 hover:bg-orange-50 transition-all flex flex-col items-center justify-center gap-1 text-slate-500"
                          >
                            {uploadingLogo ? <span className="font-bold">Uploading…</span> : data.client_logo ? (
                              <>
                                <Check size={18} className="text-green-600" />
                                <span className="font-bold text-tc-ink">Logo attached</span>
                                <span className="text-xs">Tap to replace</span>
                              </>
                            ) : (
                              <>
                                <Upload size={18} />
                                <span className="font-bold">Upload your logo</span>
                                <span className="text-xs">PNG/JPG/WEBP · up to 25MB</span>
                              </>
                            )}
                          </button>
                          <input ref={logoInputRef} type="file" accept="image/*" onChange={(e) => handleLogoUpload(e.target.files?.[0])} className="hidden" data-testid="logo-file-input" />
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>
              )}
            </div>

            {error && <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="share-error">{error}</div>}

            <div className="flex items-center justify-between mt-8">
              <button onClick={back} className="h-12 px-5 rounded-2xl border border-slate-200 font-bold text-slate-700 flex items-center gap-1" data-testid="public-prev">
                <ArrowLeft size={16} /> Back
              </button>
              {isLast ? (
                <button
                  onClick={submit}
                  disabled={stepInvalid || submitting}
                  data-testid="public-submit"
                  className="h-12 px-6 rounded-2xl text-white font-extrabold flex items-center gap-2 shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-40"
                  style={{ background: stepInvalid ? "#94A3B8" : "#f68b1f" }}
                >
                  <Send size={16} /> {submitting ? "Sending..." : "Send my story"}
                </button>
              ) : (
                <button
                  onClick={next}
                  disabled={stepInvalid}
                  data-testid="public-next"
                  className="h-12 px-6 rounded-2xl text-white font-extrabold flex items-center gap-1 shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-40"
                  style={{ background: stepInvalid ? "#94A3B8" : "#083050" }}
                >
                  Next <ArrowRight size={16} />
                </button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}

// ----- helper bits -----
const inputCls = "w-full h-12 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 text-sm font-medium";

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="tc-eyebrow text-slate-500">{label}{required && <span className="text-orange-500 ml-1">*</span>}</span>
      <div className="mt-1">{children}</div>
      {hint && <span className="block mt-1 text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

function Header() {
  return (
    <header className="glass-header">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-center gap-3">
        <img src={TC_LOGO_URL} alt="True Colors International" className="h-9 w-auto object-contain" />
      </div>
    </header>
  );
}

function Welcome({ onStart }) {
  return (
    <div className="min-h-screen bg-[var(--tc-bg-soft)] flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 py-10">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 120, damping: 18 }}
          className="tc-card p-8 sm:p-12 max-w-2xl w-full"
          data-testid="welcome-card"
        >
          <div className="tc-eyebrow text-orange-600">A note from our team</div>
          <h1 className="font-display text-3xl sm:text-4xl text-tc-ink leading-tight mt-2">
            We&rsquo;re so glad you&rsquo;re here. <span aria-hidden="true">🧡</span>
          </h1>
          <div className="space-y-4 mt-6 text-slate-700 leading-relaxed text-base sm:text-lg">
            <p>
              Thanks again for being part of a True Colors experience with your team. Someone on our team thought your story would be meaningful to hear, and we are hopeful you are willing to share it.
            </p>
            <p>
              This will only take a few minutes. There are six short questions, and you can answer them in your own words. Think of it like a quick conversation about what stood out, what shifted, and what another organization might want to know before bringing True Colors to their own team.
            </p>
            <p>
              With your permission, we may share parts of your story in a conversation with a future client, on our website, or in handouts we use to help other organizations learn more about True Colors.
            </p>
            <p>
              And because we appreciate your time, you&rsquo;ll get a small gift at the end: our <strong>{FREEBIE_TITLE}</strong> guide. It&rsquo;s a simple, helpful resource for noticing how each color shows up in everyday team moments.
            </p>
            <p className="font-semibold text-tc-ink">Ready when you are.</p>
          </div>
          <button
            onClick={onStart}
            data-testid="welcome-start"
            className="mt-8 h-14 px-8 rounded-2xl text-white font-extrabold text-base flex items-center gap-2 shadow-md hover:-translate-y-0.5 transition-all"
            style={{ background: "#f68b1f" }}
          >
            Let&rsquo;s go <ArrowRight size={18} />
          </button>
        </motion.div>
      </main>
    </div>
  );
}

function ThankYou() {
  return (
    <div className="min-h-screen bg-[var(--tc-bg-soft)] flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 py-10">
        <motion.div
          initial={{ scale: 0.92, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 160, damping: 16 }}
          className="tc-card p-8 sm:p-12 max-w-2xl w-full text-center"
          data-testid="thank-you-card"
        >
          <h1 className="font-display text-3xl sm:text-4xl text-tc-ink leading-tight">
            That made our week. <span aria-hidden="true">🧡</span>
          </h1>
          <div className="space-y-4 mt-6 text-slate-700 leading-relaxed text-base sm:text-lg">
            <p>
              Thank you for sharing your words with us. We're grateful you took the time to reflect on your True Colors experience and let us hear part of your story.
            </p>
            <p>
              Stories like yours help the next team see what's possible when people use the True Colors System to strengthen culture, communication, and connection.
            </p>
            <p>
              As promised, here's your gift. <strong>{FREEBIE_TITLE}</strong> is one of our favorite guides, and we hope it becomes one of yours too.
            </p>
          </div>
          <a
            href={FREEBIE_PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="freebie-download"
            className="mt-8 inline-flex items-center justify-center gap-2 h-14 px-8 rounded-2xl text-white font-extrabold text-base shadow-md hover:-translate-y-0.5 transition-all"
            style={{ background: "#f68b1f" }}
          >
            <Download size={18} /> Download the guide
          </a>
          <p className="mt-10 text-slate-600 text-sm sm:text-base">
            Want to help another team experience this kind of impact? We'd love to help them use True Colors to strengthen their culture, one conversation and one team moment at a time.
          </p>
          <a
            href="https://truecolorsintl.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="visit-truecolors"
            className="mt-3 inline-flex items-center gap-1.5 text-tc-ink font-bold underline underline-offset-4 decoration-2"
            style={{ textDecorationColor: "#f68b1f" }}
          >
            truecolorsintl.com <ExternalLink size={14} />
          </a>
        </motion.div>
      </main>
    </div>
  );
}
